import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, Clock, Phone, Store, Banknote, CreditCard, Coins, ShieldCheck } from "lucide-react";
import { GlassCard, SectionTitle } from "../components/common/ui";
import AgeBadge from "../components/common/AgeBadge";
import StoreBranding from "../components/common/StoreBranding";
import api from "../lib/api";

const PAYMENTS = [
  { icon: Coins, label: "Cash", text: "Pay with cash in store when you collect." },
  { icon: CreditCard, label: "Card", text: "Debit and credit cards accepted on collection." },
  { icon: Banknote, label: "Bank transfer", text: "Pay in advance by bank transfer using your reservation number as the reference." },
];

export default function ClickCollect() {
  const [page, setPage] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Click & Collect · Clacton Fireworks";
    Promise.all([
      api.get("/content/pages/click-and-collect").then(({ data }) => data).catch(() => null),
      api.get("/stores").then(({ data }) => data).catch(() => []),
    ]).then(([p, s]) => { setPage(p); setStores(s || []); }).finally(() => setLoading(false));
    return () => { document.title = "Clacton Fireworks"; };
  }, []);

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-20" data-testid="click-collect-page">
      <h1 className="font-playfair text-3xl sm:text-4xl text-white">{page?.title || "Click & Collect"}</h1>
      {page?.content_html && (
        <div className="mt-6 prose prose-invert max-w-none text-white/80" dangerouslySetInnerHTML={{ __html: page.content_html }} />
      )}

      {/* Payment options */}
      <div className="mt-12">
        <SectionTitle eyebrow="Payment" title="How you can pay on collection" subtitle="No payment is taken online — you pay when you collect your fireworks in store." />
        <div className="mt-8 grid gap-5 sm:grid-cols-3" data-testid="payment-options">
          {PAYMENTS.map((p) => (
            <GlassCard key={p.label} className="p-6" data-testid={`payment-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="grid place-items-center h-11 w-11 rounded-xl bg-gold/10 border border-gold/20"><p.icon className="h-5 w-5 text-gold" /></div>
              <h3 className="mt-4 font-playfair text-lg text-white">{p.label}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.text}</p>
            </GlassCard>
          ))}
        </div>
      </div>

      {/* Stores */}
      <div className="mt-14">
        <SectionTitle eyebrow="Collection points" title="Where to collect" subtitle="Reserve online and collect at one of our stores." />
        {stores.length === 0 ? (
          <GlassCard className="mt-8 p-10 text-center text-muted-foreground" hover={false}>Collection point details are being finalised — please check back soon.</GlassCard>
        ) : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="stores-list">
            {stores.map((s) => (
              <Link key={s.store_id} to={`/stores/${s.slug}`} className="block group">
                <GlassCard className="p-6 h-full" data-testid={`store-card-${s.store_id}`}>
                  <div className="flex items-center gap-2 text-gold"><Store className="h-4 w-4" /><span className="text-xs uppercase tracking-wide">Collection Point</span></div>
                  <StoreBranding store={s} className="mt-3" />
                  <span className="mt-4 inline-block text-sm font-semibold text-gold">View store details →</span>
                </GlassCard>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="cc-age-notice">
        <AgeBadge size={44} linked />
        <span className="text-sm text-white/50">Please bring valid photo ID when you collect.</span>
      </div>

      <div className="mt-6">
        <Link to="/areas-we-serve" data-testid="cc-areas-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold hover:opacity-90">
          <MapPin className="h-4 w-4" /> See all the areas we serve
        </Link>
      </div>
    </div>
  );
}
